import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableRow, IconButton } from '@mui/material';
import { Delete, Edit, Close, Save } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { AUTH_LEVELS } from '../../redux/constants/roles';
import { groupListByProperty } from '../../utils/collections';
import { dateTime } from '../../utils/parsing';
import OrganizationSelector from '../userMgmt/OrganizationSelector';
import ImageUpload from './ImageUpload';
import BuildingFieldsEdit from './BuildingFieldsEdit';
import {
  patchBuildingActionCreator,
  deleteBuildingActionCreator,
} from '../../redux/actions/buildings';

/**
 * Component that displays buildings and allows for inline editing of the organization,
   building fields and also permanent building deletion.
 */
const BuildingsTable = ({
  buildingsByOrg,
  organizations,
  latestTimestamps,
  palette,
  patchBuilding,
  deleteBuilding,
  isSuper,
}) => {
  const [editingId, setEditingId] = useState();
  const [changes, setChanges] = useState({});
  const confirm = useConfirm();
  return (
    <div className="buildings-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Name</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Equipment</TableCell>
            <TableCell>Points</TableCell>
            <TableCell>Image</TableCell>
            <TableCell>Last data ingest</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(buildingsByOrg).map((orgId) => [
            <TableRow
              className="org-row"
              key={`org-${orgId}`}
              style={{ background: palette.primary.main }}
            >
              <TableCell />
              <TableCell colSpan={7}>{organizations[orgId]?.name}</TableCell>
            </TableRow>,
            buildingsByOrg[orgId]?.map((building) => (
              <React.Fragment key={building.id}>
                <TableRow hover className="building-row">
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setEditingId(building.id);
                        setChanges({});
                      }}
                      size="large"
                    >
                      <Edit />
                    </IconButton>
                    {isSuper ? (
                      <IconButton
                        onClick={() =>
                          confirm({
                            title: `Delete building ${building.name}?`,
                            description:
                              'This action cannot be undone, and will remove all equipment and data from the system.',
                            confirmationText: `Delete ${building.name}`,
                          })
                            .then(({ confirmed }) =>
                              confirmed ? deleteBuilding(building.id) : null
                            )
                            .catch(() => {})
                        }
                        size="large"
                      >
                        <Delete />
                      </IconButton>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {building.name} (id={building.id})
                  </TableCell>
                  <TableCell>
                    {isSuper ? (
                      <OrganizationSelector
                        selected={building.orgId}
                        onValueChange={(newOrgId) =>
                          patchBuilding(building.id, { org_id: newOrgId })
                        }
                      />
                    ) : (
                      organizations[orgId]?.name
                    )}
                  </TableCell>
                  <TableCell>{building.equipCount}</TableCell>
                  <TableCell>{building.pointCount}</TableCell>
                  <TableCell>
                    <ImageUpload building={building} />
                  </TableCell>
                  <TableCell>
                    {latestTimestamps[building.id]
                      ? dateTime(latestTimestamps[building.id])
                      : 'Never'}
                  </TableCell>
                </TableRow>
                {editingId === building.id && (
                  <TableRow>
                    <TableCell>
                      <IconButton onClick={() => setEditingId(null)} size="large">
                        <Close />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() =>
                          patchBuilding(building.id, changes).then(() => setChanges({}))
                        }
                        size="large"
                      >
                        <Save />
                      </IconButton>
                    </TableCell>
                    <TableCell colSpan={6}>
                      <BuildingFieldsEdit
                        building={building}
                        changes={changes}
                        setChanges={(newChanges) =>
                          setChanges((prev) => ({ ...prev, ...newChanges }))
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )),
          ])}
        </TableBody>
      </Table>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: {
      buildings: { byId },
      ingestStats: { latestTimestamps },
    },
    users: { orgsById },
    user: { userAuthLevel },
    ui: { palette },
  } = state;
  return {
    isSuper: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
    buildingsByOrg: groupListByProperty('orgId', Object.values(byId)),
    organizations: orgsById,
    latestTimestamps,
    palette,
  };
};

const mapDispatchToProps = (dispatch) => ({
  patchBuilding: (buildingId, patch) => dispatch(patchBuildingActionCreator(buildingId, patch)),
  deleteBuilding: (buildingId) => dispatch(deleteBuildingActionCreator(buildingId)),
});
export default connect(mapStateToProps, mapDispatchToProps)(BuildingsTable);
